// src/hooks/useTranslations.ts
import { useCallback } from 'react';
import { SupportedLanguage } from '@/config/routes';
import { getContentForLanguage } from '@/locales/translations/content';
import { SEO_CONFIG } from '@/config/seo';
import { useLocalization } from './useLocalization';

type TranslationSections = 'fullscreen' | 'resolution' | 'aria' | 'buttons' | 'messages' | 'tooltips';

interface MetaData {
  title: string;
  description: string;
  keywords: string[];
}

export const useTranslations = () => {
  const { currentLang } = useLocalization();

  const t = useCallback((
    section: TranslationSections,
    key: string,
    replacements: Record<string, string> = {}
  ): string => {
    const content = getContentForLanguage(currentLang as SupportedLanguage);
    const sectionContent = content[section];
    let translation = '';

    if (sectionContent && typeof sectionContent === 'object') {
      translation = (sectionContent as Record<string, string>)[key] || '';
    }

    // Replace any placeholders with values
    Object.entries(replacements).forEach(([key, value]) => {
      translation = translation.replace(`{${key}}`, value);
    });

    return translation;
  }, [currentLang]);

  // Convert readonly array to mutable array using spread operator
  const meta: MetaData = {
    title: SEO_CONFIG.meta[currentLang].title,
    description: SEO_CONFIG.meta[currentLang].description,
    keywords: [...SEO_CONFIG.meta[currentLang].keywords]
  };
  
  const content = getContentForLanguage(currentLang as SupportedLanguage);

  return {
    t,
    meta,
    content,
    currentLang
  };
};